/**
 * Flavor landing page (sections/flavor-lp.liquid)
 *  - step 1: whey or plant
 *  - step 2: how many packs of this page's flavor (stepper)
 *  - step 3: subscribe or one-time
 *  - live totals, then one cart call and straight to checkout (or the cart
 *    drawer), applying an optional promo code through /discount/CODE.
 *
 * All prices arrive in the JSON config the section renders from live variants;
 * nothing is fetched. Cart lines use _p_id / _p_id_link like the protein
 * builder so the cart nests the flavor packs under the protein.
 *
 * URL params: ?base=whey|plant, ?packs=N, ?plan=subscribe|onetime.
 */
(() => {
  const money = (cents) => `$${(Math.round(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const randomId = () => Array.from({ length: 6 }, () => 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]).join('');
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  function init(root) {
    if (!root || root.dataset.flpInit) return;
    root.dataset.flpInit = '1';

    let cfg;
    try {
      cfg = JSON.parse(root.querySelector('[data-flp-config]').textContent);
    } catch {
      return;
    }
    const flavor = cfg.flavor || {};
    const maxPacks = Math.max(1, Number(cfg.maxPacks) || 6);
    const storeKey = `flp:${root.dataset.sectionId || 'lp'}:${flavor.variantId || ''}`;
    const params = new URLSearchParams(window.location.search);

    const state = {
      base: null,
      packs: clamp(Number(cfg.defaultPacks) || 2, 1, maxPacks),
      plan: cfg.defaultPlan === 'onetime' ? 'onetime' : 'subscribe',
    };

    // ----- elements
    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));
    const baseInputs = $$('[data-flp-base]');
    const planInputs = $$('[data-flp-plan]');
    const dec = $('[data-flp-packs-dec]');
    const inc = $('[data-flp-packs-inc]');
    const packsText = $('[data-flp-packs-text]');
    const packHint = $('[data-flp-pack-hint]');
    const sum = {
      base: $('[data-flp-sum-base]'),
      basePrice: $('[data-flp-sum-base-price]'),
      packs: $('[data-flp-sum-packs]'),
      packsPrice: $('[data-flp-sum-packs-price]'),
      subRow: $('[data-flp-sum-subrow]'),
      sub: $('[data-flp-sum-sub]'),
      promoRow: $('[data-flp-sum-promorow]'),
      promo: $('[data-flp-sum-promo]'),
      total: $('[data-flp-total]'),
      was: $('[data-flp-was]'),
      save: $('[data-flp-save]'),
      error: $('[data-flp-error]'),
    };
    const planTotals = { subscribe: $('[data-flp-plan-total="subscribe"]'), onetime: $('[data-flp-plan-total="onetime"]') };
    const planWas = { subscribe: $('[data-flp-plan-was="subscribe"]') };
    const bar = $('[data-flp-bar]');
    const barTotal = $('[data-flp-bar-total]');
    const barNote = $('[data-flp-bar-note]');
    const ctas = $$('[data-flp-checkout]');
    const heroPrice = $('[data-flp-hero-price]');
    const heroTotal = $('[data-flp-hero-total]');
    const heroWas = $('[data-flp-hero-was]');
    const kit = $('.flp-kit') || root;

    // ----- math
    const protein = () => (state.base && cfg.proteins ? cfg.proteins[state.base] : null);
    const packWord = (n) => (n === 1 ? cfg.text.packOne : cfg.text.packMany);
    function promoFor(plan, subtotal) {
      const p = cfg.promo || {};
      if (!p.code || !p.percent) return 0;
      if (plan !== 'subscribe' && p.applies !== 'both') return 0;
      // Floor so the estimate never undercuts what checkout charges.
      return Math.floor((subtotal * Number(p.percent)) / 100);
    }
    function totals(plan = state.plan, base = protein(), packs = state.packs) {
      const sub = plan === 'subscribe';
      const basePrice = base ? (sub ? base.subPrice : base.price) : 0;
      const baseFull = base ? base.price : 0;
      const packsTotal = (Number(flavor.price) || 0) * packs;
      const subtotal = basePrice + packsTotal;
      const promo = promoFor(plan, subtotal);
      const total = subtotal - promo;
      const full = baseFull + packsTotal;
      return { sub, basePrice, baseFull, packsTotal, subtotal, subSavings: baseFull - basePrice, promo, total, full, savings: full - total };
    }
    // Hero "from" price: cheapest available protein with the default packs, subscribed.
    function heroFrom() {
      const bases = Object.values(cfg.proteins || {}).filter((b) => b && b.available && b.variantId);
      if (!bases.length || !flavor.variantId) return null;
      const packs = clamp(Number(cfg.defaultPacks) || 2, 1, maxPacks);
      return bases.map((b) => totals('subscribe', b, packs)).sort((a, b) => a.total - b.total)[0];
    }

    // ----- render
    function render() {
      root.dataset.base = state.base || '';
      baseInputs.forEach((input) => { input.checked = input.value === state.base; });
      planInputs.forEach((input) => { input.checked = input.value === state.plan; });

      if (packsText) packsText.textContent = `${state.packs} ${packWord(state.packs)}`;
      if (dec) dec.disabled = state.packs <= 1;
      if (inc) inc.disabled = state.packs >= maxPacks;
      if (packHint) {
        const packets = state.packs * 7;
        packHint.innerHTML = `<strong>${packets} packets</strong> &middot; about ${packets} shakes`;
      }

      const base = protein();
      const t = totals();

      // Plan prices, each computed for the current protein and pack count.
      const tSub = totals('subscribe');
      const tOtb = totals('onetime');
      if (planTotals.subscribe) planTotals.subscribe.textContent = base ? money(tSub.total) : '';
      if (planWas.subscribe) planWas.subscribe.textContent = base && tSub.total < tSub.full ? money(tSub.full) : '';
      if (planTotals.onetime) planTotals.onetime.textContent = base ? money(tOtb.total) : '';

      // Summary
      if (sum.base) sum.base.textContent = base ? base.title : cfg.text.noBase;
      if (sum.basePrice) sum.basePrice.textContent = base ? money(t.basePrice) : '';
      if (sum.packs) sum.packs.textContent = `${state.packs} × ${flavor.title || 'Flavor'}`;
      if (sum.packsPrice) sum.packsPrice.textContent = money(t.packsTotal);
      if (sum.subRow) sum.subRow.hidden = !(base && t.sub && t.subSavings > 0);
      if (sum.sub) sum.sub.textContent = `−${money(t.subSavings)}`;
      if (sum.promoRow) sum.promoRow.hidden = !(base && t.promo > 0);
      if (sum.promo) sum.promo.textContent = `−${money(t.promo)}`;
      if (sum.total) sum.total.textContent = money(t.total);
      if (sum.was) { sum.was.hidden = !(base && t.savings > 0); sum.was.textContent = money(t.full); }
      if (sum.save) { sum.save.hidden = !(base && t.savings > 0); sum.save.textContent = `You save ${money(t.savings)}`; }

      // Buttons
      const readyText = cfg.after === 'drawer' ? cfg.text.readyDrawer : cfg.text.ready;
      const label = base ? `${readyText} · ${money(t.total)}` : cfg.text.needBase;
      ctas.forEach((btn) => { if (!btn.dataset.busy) btn.textContent = label; });

      // Mobile bar
      if (barTotal) barTotal.textContent = base ? money(t.total) : '';
      if (barNote) barNote.textContent = base ? `${base.label} + ${state.packs} ${packWord(state.packs)}` : cfg.text.noBase;
    }

    function persist() {
      try { sessionStorage.setItem(storeKey, JSON.stringify(state)); } catch { /* private mode */ }
    }
    function update() { render(); persist(); }

    function flash(step) {
      const el = root.querySelector(`[data-flp-step="${step}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.remove('is-flash');
      void el.offsetWidth;
      el.classList.add('is-flash');
      el.addEventListener('animationend', () => el.classList.remove('is-flash'), { once: true });
    }
    function showError(msg) {
      if (!sum.error) return;
      sum.error.textContent = msg || '';
      sum.error.hidden = !msg;
    }

    // ----- actions
    function pickBase(key, { track = true } = {}) {
      const b = cfg.proteins && cfg.proteins[key];
      if (!b || !b.available) return;
      state.base = key;
      update();
      if (track) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'flp_pick_protein', flavor: flavor.title, protein: key });
      }
    }
    function setPacks(n) {
      state.packs = clamp(Number(n) || 1, 1, maxPacks);
      update();
    }

    async function checkout(btn) {
      if (btn.dataset.busy) return;
      showError('');
      const base = protein();
      if (!base) { flash('base'); return; }
      if (!flavor.variantId || !flavor.available) { showError(cfg.text.cartError); return; }
      const sub = state.plan === 'subscribe';
      if (sub && !base.planId) { showError(cfg.text.noPlan); flash('plan'); return; }

      const pid = randomId();
      const items = [{ id: base.variantId, quantity: 1, properties: { _source: cfg.source, _p_id: pid } }];
      if (sub) items[0].selling_plan = base.planId;
      const pack = { id: flavor.variantId, quantity: state.packs, properties: { _source: cfg.source, _p_id_link: pid } };
      if (state.packs > 1) pack.properties._p_quantity = String(state.packs);
      if (sub && flavor.planId) pack.selling_plan = flavor.planId;
      items.push(pack);

      const t = totals();
      const label = btn.textContent;
      ctas.forEach((b) => { b.dataset.busy = '1'; b.classList.add('is-loading'); b.textContent = 'One sec…'; });
      try {
        if (cfg.clearCart) {
          await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
        }
        let ok = false;
        const viaStore = window._cart && typeof window._cart.add === 'function';
        if (viaStore) {
          const res = await window._cart.add({ items });
          ok = !(res && res.cart_error);
        } else {
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          ok = r.ok;
        }
        if (!ok) throw new Error('cart_error');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'flp_add',
          flavor: flavor.title,
          protein: state.base,
          packs: state.packs,
          purchase_type: sub ? 'subscription' : 'onetime',
          value: t.total / 100,
          promo_code: t.promo > 0 ? cfg.promo.code : '',
          destination: cfg.after,
        });
        if (cfg.after === 'drawer' && viaStore) {
          try { window._stores?.modal?.setId?.('cart-drawer'); } catch { /* no drawer */ }
          ctas.forEach((b) => { b.textContent = cfg.text.added; });
          setTimeout(() => {
            ctas.forEach((b) => { delete b.dataset.busy; b.classList.remove('is-loading'); });
            render();
          }, 2000);
          return;
        }
        try { sessionStorage.removeItem(storeKey); } catch { /* ignore */ }
        const code = t.promo > 0 ? cfg.promo.code : '';
        window.location.href = code
          ? `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/checkout')}`
          : '/checkout';
      } catch {
        showError(cfg.text.cartError);
        ctas.forEach((b) => { delete b.dataset.busy; b.classList.remove('is-loading'); b.textContent = label; });
      }
    }

    // ----- wiring
    baseInputs.forEach((input) => input.addEventListener('change', () => pickBase(input.value)));
    planInputs.forEach((input) => input.addEventListener('change', () => { state.plan = input.value === 'onetime' ? 'onetime' : 'subscribe'; update(); }));
    if (dec) dec.addEventListener('click', () => setPacks(state.packs - 1));
    if (inc) inc.addEventListener('click', () => setPacks(state.packs + 1));
    ctas.forEach((btn) => btn.addEventListener('click', () => checkout(btn)));

    // Hero / recipe buttons scroll to the kit; a recipe button can also set the pack count.
    $$('[data-flp-jump]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        if (el.dataset.flpPacks) setPacks(el.dataset.flpPacks);
        kit.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'flp_jump', flavor: flavor.title, from: el.closest('.flp-recipe') ? 'recipe' : 'hero' });
      });
    });

    // Mobile bar: visible while the kit is on screen.
    if (bar && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          bar.classList.toggle('is-visible', entry.isIntersecting);
          bar.setAttribute('aria-hidden', entry.isIntersecting ? 'false' : 'true');
        });
      }, { threshold: 0.05 });
      io.observe(kit);
    }

    // ----- initial state: URL params > saved session > section defaults
    try {
      const saved = JSON.parse(sessionStorage.getItem(storeKey) || 'null');
      if (saved) {
        if (saved.base && cfg.proteins[saved.base]) state.base = saved.base;
        if (saved.packs) state.packs = clamp(Number(saved.packs), 1, maxPacks);
        if (saved.plan) state.plan = saved.plan === 'onetime' ? 'onetime' : 'subscribe';
      }
    } catch { /* ignore */ }
    const qBase = params.get('base');
    const qPacks = params.get('packs');
    const qPlan = params.get('plan');
    if (qBase && cfg.proteins[qBase]) state.base = qBase;
    else if (!state.base && cfg.defaultBase && cfg.proteins[cfg.defaultBase]) state.base = cfg.defaultBase;
    if (state.base && !(cfg.proteins[state.base] && cfg.proteins[state.base].available)) state.base = null;
    if (qPacks) state.packs = clamp(Number(qPacks) || state.packs, 1, maxPacks);
    if (qPlan === 'onetime' || qPlan === 'subscribe') state.plan = qPlan;

    const from = heroFrom();
    if (from && heroPrice) {
      heroPrice.hidden = false;
      if (heroTotal) heroTotal.textContent = money(from.total);
      if (heroWas) heroWas.textContent = from.total < from.full ? money(from.full) : '';
    }
    render();
  }

  const boot = () => document.querySelectorAll('[data-section-type="flavor-lp"]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target && e.target.querySelector('[data-section-type="flavor-lp"]');
    if (root) init(root);
  });
})();
